import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)] py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="font-display font-extrabold text-2xl tracking-tight text-[#1C2A39]">Brite<span className="text-[#0D9488]">lines</span></span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`font-body text-sm font-medium relative transition-colors duration-300 ${
                location.pathname === l.to ? "text-[#0D9488]" : "text-[#1C2A39] hover:text-[#0D9488]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a href={`tel:${CONTACT.phoneRaw}`} data-testid="nav-call-btn" className="btn-ghost !py-2.5 !px-4 text-sm">
            <Phone size={16} /> {CONTACT.phone}
          </a>
          <Link to="/quote" data-testid="nav-quote-btn" className="btn-primary !py-2.5 !px-5 text-sm">Get a Free Quote</Link>
        </div>

        <button data-testid="mobile-menu-btn" className="md:hidden text-[#1C2A39]" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden bg-white overflow-hidden border-t border-slate-100"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="font-display text-2xl font-medium text-[#1C2A39]">{l.label}</Link>
              ))}
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-ghost justify-center mt-2"><Phone size={16} /> {CONTACT.phone}</a>
              <Link to="/quote" className="btn-primary justify-center">Get a Free Quote</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
